import { useEffect, type Dispatch, type SetStateAction } from "react";
import type { Locale } from "../../shared/dto";
import { translateCount, translateCountLabel } from "../i18n";
import { StaffClientAvatar, StaffStepLegend, StaffStepStrip, StaffSurfaceState } from "./index";
import { formatDate, staffCurrentStepTitle } from "./core";
import type { StaffMatrixClient, StaffRole } from "./model";
import {
  STAFF_TEST_FILTER_STORAGE_KEY,
  readStoredStaffTestFilter,
  storeStaffTestFilter,
  visibleTestCount
} from "./test-filter";
import { ClientNameLine } from "./TestFilterNote";
import { clientHandleText, clientRowHandles, type ClientRowHandle } from "./client-identity";
import "./client-identity.css";
import "./client-matrix-facts.css";
import { staffOwnerLabel } from "./owner-label";
import { PARTNER_FILTER_NONE, partnerFilterOptions, type PartnerFilter } from "./partner-filter";
import "./partner-filter.css";

export { STAFF_TEST_FILTER_STORAGE_KEY } from "./test-filter";

export type ClientListFilter = "pending" | "calls" | "unassigned" | "stuck" | null;

export function ClientList(props: {
  role: StaffRole;
  locale: Locale;
  t: (key: string) => string;
  /** The rows the partner filter left (all rows when it is off). */
  matrixClients: StaffMatrixClient[];
  /** Every row before the partner filter: the partner choices come from it. */
  allClients?: StaffMatrixClient[];
  partnerFilter?: PartnerFilter;
  setPartnerFilter?: Dispatch<SetStateAction<PartnerFilter>>;
  visibleClients: StaffMatrixClient[];
  query: string;
  setQuery: Dispatch<SetStateAction<string>>;
  loading: boolean;
  selectedId: string | null;
  hideTestTenants: boolean;
  filter: ClientListFilter;
  setFilter: Dispatch<SetStateAction<ClientListFilter>>;
  setHideTestTenants: Dispatch<SetStateAction<boolean>>;
  stuck: (client: StaffMatrixClient) => boolean;
  waitingOverdue: (client: StaffMatrixClient) => boolean;
  openClient: (clientId: string, step?: string | null) => void;
}) {
  const { filter, hideTestTenants, loading, matrixClients, openClient, query, selectedId, setFilter, setHideTestTenants, setQuery, stuck, visibleClients, waitingOverdue } = props;
  const allClients = props.allClients ?? matrixClients;
  const partnerFilter = props.partnerFilter ?? null;
  const setPartnerFilter = (next: PartnerFilter) => props.setPartnerFilter?.(next);
  const feedbackTotal = matrixClients.reduce((sum, client) => sum + client.pendingFeedback, 0);
  // The empty state answers what the partner just DID. One `emptyMatrixHint`
  // served all three, so a search with no result was titled "Hledání nemá
  // žádný výsledek" and advised "Změň výběr" - about a filter nobody had
  // touched (audit P-19). Title, hint and the way back come from the same
  // branch, so they can no longer describe two different actions.
  const emptyMatrixKind = query.trim() ? "Search" : filter || partnerFilter !== null ? "Filter" : "All";
  const partnerOptions = props.role === "admin" ? partnerFilterOptions(allClients, props.locale) : [];
  const unassignedCount = allClients.filter((client) => !client.partner).length;
  // The choice of a partner who has since lost every client is still shown, so
  // the select never silently jumps back to "all" under the reader's hand.
  const partnerChoiceMissing = partnerFilter !== null && partnerFilter !== PARTNER_FILTER_NONE
    && !partnerOptions.some((option) => option.id === partnerFilter);
  const emptyMatrixTitle = props.t(`staffConsole.emptyMatrix${emptyMatrixKind}`);
  const emptyMatrixHint = props.t(`staffConsole.emptyMatrix${emptyMatrixKind}Hint`);
  const shownTests = visibleTestCount(matrixClients);
  // F138 vada 11: two records of one person render the same name AND the same
  // address here too, and "Otevřít klienta" then leads to two different
  // clients with no way to tell which. Computed over the rows the reader can
  // SEE, so a filtered-away twin leaves the survivor unmarked.
  const handles = clientRowHandles(visibleClients.map((client) => ({
    clientId: client.id,
    name: client.name,
    email: client.email,
    createdAt: client.createdAt
  })));
  useEffect(() => {
    const stored = readStoredStaffTestFilter(hideTestTenants);
    if (stored !== hideTestTenants) setHideTestTenants(stored);
  }, [hideTestTenants, setHideTestTenants]);
  return <>
      {/* Metrics double as matrix filters: clicking answers "who needs me
          now" without scanning 59 rows. */}
      <section className="staff-metrics" aria-label={props.t("staffConsole.summary")}>
        <Metric value={feedbackTotal} label={translateCountLabel(props.locale, "staffConsole.feedbackLabel", feedbackTotal)}
          active={filter === "pending"} onClick={() => setFilter(filter === "pending" ? null : "pending")} />
        <Metric value={matrixClients.filter((client) => client.nextCall).length} label={props.t("staffConsole.metricCalls")}
          active={filter === "calls"} onClick={() => setFilter(filter === "calls" ? null : "calls")} />
        {props.role === "admin" && <Metric value={matrixClients.filter((client) => !client.partner).length} label={props.t("staffConsole.metricUnassigned")} tone="warn"
          active={filter === "unassigned"} onClick={() => setFilter(filter === "unassigned" ? null : "unassigned")} />}
        {props.role === "admin" && <Metric value={matrixClients.filter(stuck).length} label={props.t("staffConsole.metricStuck")} tone="warn"
          active={filter === "stuck"} onClick={() => setFilter(filter === "stuck" ? null : "stuck")} />}
      </section>

      <section className="staff-matrix-panel" aria-labelledby="staff-matrix-title">
        <div className="staff-section-heading">
          <div>
            <h1 id="staff-matrix-title">{props.t("staffConsole.matrixTitleCount").replace("{count}", String(matrixClients.length))}</h1>
            {/* The heading count grew from (4) to (21) when the filter came
                off and said nothing about why (audit P-12). */}
            {shownTests > 0 && (
              <p className="staff-matrix-shown-test">
                {translateCount(props.locale, "staffConsole.matrixShownTest", shownTests)}
              </p>
            )}
          </div>
          <label className="checkbox-row" style={{ minHeight: 44 }}>
            <input
              data-staff-test-filter
              type="checkbox"
              checked={hideTestTenants}
              onChange={(event) => {
                const next = event.target.checked;
                setHideTestTenants(next);
                storeStaffTestFilter(next);
              }}
            />
            <span>{props.t("staffConsole.hideTestTenants")}</span>
          </label>
          {props.role === "admin" && (
            <label className="staff-partner-filter">
              <span className="staff-partner-filter-label">{props.t("staffConsole.partnerFilter")}</span>
              <select
                data-staff-partner-filter
                value={partnerFilter ?? ""}
                onChange={(event) => setPartnerFilter(event.target.value === "" ? null : event.target.value)}
              >
                <option value="">{props.t("staffConsole.partnerFilterAll")}</option>
                {partnerOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {`${option.name?.trim() || props.t("staffConsole.partnerFilterUnnamed")} (${option.count})`}
                  </option>
                ))}
                {unassignedCount > 0 && (
                  <option value={PARTNER_FILTER_NONE}>
                    {`${props.t("staffConsole.partnerFilterNone")} (${unassignedCount})`}
                  </option>
                )}
                {(partnerChoiceMissing || (partnerFilter === PARTNER_FILTER_NONE && unassignedCount === 0)) && (
                  <option value={partnerFilter ?? ""}>{props.t("staffConsole.partnerFilterGone")}</option>
                )}
              </select>
            </label>
          )}
          <label className="staff-search">
            <span className="visually-hidden">{props.t("staffConsole.search")}</span>
            <span className="staff-search-field">
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="6.5" />
                <path d="m16 16 4 4" />
              </svg>
              <input
                aria-label={props.t("staffConsole.search")}
                placeholder={props.t("staffConsole.searchPlaceholder")}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                type="search"
              />
            </span>
          </label>
        </div>
        {loading ? (
          <StaffSurfaceState
            body={props.t("partnerOS.states.matrixLoadingBody")}
            title={props.t("partnerOS.states.matrixLoadingTitle")}
            variant="loading"
          />
        ) : (<>
          {visibleClients.length === 0 && (
            <StaffSurfaceState
              body={emptyMatrixHint}
              title={emptyMatrixTitle}
              variant="empty"
              action={emptyMatrixKind === "Search" ? (
                <button className="secondary-button compact-button" type="button" onClick={() => setQuery("")}>
                  {props.t("staffConsole.clearMatrixSearch")}
                </button>
              ) : emptyMatrixKind === "Filter" ? (
                // The filter state told the partner to change the selection and
                // then offered no control to change it with.
                <button className="secondary-button compact-button" type="button" onClick={() => { setFilter(null); setPartnerFilter(null); }}>
                  {props.t("staffConsole.clearMatrixFilter")}
                </button>
              ) : undefined}
            />
          )}
          {/* Mobile: the 2400px matrix table hid everything except names
              behind a sideways scroll. Cards show the same facts stacked;
              CSS swaps table vs cards at 900px (night round N16). */}
          <div className="staff-matrix-cards">
            {visibleClients.length > 0 && <p className="staff-mobile-queue-label">{props.t("staffConsole.mobileQueueSorted")}</p>}
            {visibleClients.map((client) => {
              const open = () => openClient(client.id);
              const overdue = waitingOverdue(client);
              return (
                <article key={client.id} className={`matrix-card${selectedId === client.id ? " selected" : ""}`}>
                  <div className="matrix-card-head">
                    <button className="matrix-client-button" type="button" onClick={open}>
                      <StaffClientAvatar name={client.name} email={client.email} photoUrl={client.photoUrl} />
                      <span className="matrix-client-meta">
                        <ClientNameLine isTest={client.is_test} name={client.name} t={props.t} />
                        <small>{client.email}</small>
                        <ClientRecordMark client={client} handles={handles} locale={props.locale} t={props.t} />
                      </span>
                    </button>
                    {client.pendingFeedback > 0 && (
                      <button className={`pending-count active pending-count-button${overdue ? " overdue" : ""}`} type="button"
                        title={props.t("staffConsole.openFeedback")}
                        onClick={() => openClient(client.id, "feedback")}>
                        {translateCount(props.locale, "staffConsole.feedbackCount", client.pendingFeedback)}
                      </button>
                    )}
                  </div>
                  <div className="matrix-card-facts">
                    <span className="phase-label">{staffCurrentStepTitle(client.steps, props.t)}</span>
                    <span className={`matrix-card-age${stuck(client) ? " age-stuck" : ""}`}>{matrixSectionAge(client, props.locale, props.t)}</span>
                    <span className={`owner-pill owner-${matrixOwner(client)}`}>
                      {staffOwnerLabel(matrixOwner(client), props.role, client.partner?.name, props.t)}
                    </span>
                  </div>
                  <p className="matrix-card-call">{props.t("staffConsole.nextCall")}: {client.nextCall ? formatDate(client.nextCall.startsAt, props.locale) : props.t("staffConsole.none").toLocaleLowerCase()}</p>
                  <StaffStepStrip steps={client.steps} t={props.t} />
                </article>
              );
            })}
          </div>
          <div className="staff-table-scroll">
            <table className="staff-matrix">
              <thead>
                <tr>
                  <th>{props.t("staffConsole.client")}</th>
                  <th>{props.t("staffConsole.currentPhase")}</th>
                  <th>{props.t("staffConsole.onDeck")}</th>
                  <th>{props.t("staffConsole.sectionAge")}</th>
                  <th>{props.t("staffConsole.nextCall")}</th>
                  <th>{props.t("staffConsole.pending")}</th>
                  <th>{props.t("staffConsole.stepProgress")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleClients.map((client) => (
                  // Whole row = click target (the name button alone was a
                  // small island); the pending chip keeps its own action.
                  <tr
                    key={client.id}
                    className={`matrix-row${selectedId === client.id ? " selected" : ""}`}
                    onClick={(event) => {
                      if (event.target instanceof Element && event.target.closest("button") && !(event.target as Element).closest(".matrix-client-button")) return;
                      openClient(client.id);
                    }}
                  >
                    <td>
                      {/* The KLIENT column has a fixed width so one long name
                          can never push POSTUP off the card (audit P-04); the
                          NAME ellipsizes inside `ClientNameLine` and keeps its
                          full text in the title, where a hover still reads it.
                          The TESTOVACÍ badge stands beside the cut, not behind
                          it (audit2 N01). */}
                      <button className="matrix-client-button" type="button" onClick={() => openClient(client.id)}>
                        <StaffClientAvatar name={client.name} email={client.email} photoUrl={client.photoUrl} />
                        <span className="matrix-client-meta">
                          <ClientNameLine isTest={client.is_test} name={client.name} t={props.t} />
                          <small title={client.email}>{client.email}</small>
                          <ClientRecordMark client={client} handles={handles} locale={props.locale} t={props.t} />
                        </span>
                      </button>
                    </td>
                    <td><span className="phase-label">{staffCurrentStepTitle(client.steps, props.t)}</span></td>
                    {/* Who has the ball: the single fact an operator scans a
                        list for, previously derivable only from the queue. */}
                    <td>
                      <span className={`owner-pill owner-${matrixOwner(client)}`}>
                        {staffOwnerLabel(matrixOwner(client), props.role, client.partner?.name, props.t)}
                      </span>
                    </td>
                    <td className={stuck(client) ? "age-stuck" : undefined}>{matrixSectionAge(client, props.locale, props.t)}</td>
                    <td>{client.nextCall ? formatDate(client.nextCall.startsAt, props.locale) : props.t("staffConsole.none")}</td>
                    <td>
                      {client.pendingFeedback > 0 ? (
                        <button className={`pending-count active pending-count-button${waitingOverdue(client) ? " overdue" : ""}`} type="button"
                          title={props.t("staffConsole.openFeedback")}
                          onClick={() => openClient(client.id, "feedback")}>
                          {client.pendingFeedback}
                        </button>
                      ) : <span className="pending-count">0</span>}
                    </td>
                    <td><StaffStepStrip steps={client.steps} t={props.t} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* The legend teaches below the data; above it, it shouted on every
              open even for partners who learned it weeks ago. */}
          {visibleClients.length > 0 && <StaffStepLegend t={props.t} />}
        </>)}
      </section>
  </>;
}

/**
 * Which record this row stands for, or "" when the row has no twin on screen.
 * The list prints the address itself, so a handle that IS the address adds
 * nothing and stays silent (F138 vada 11).
 */
export function clientRecordMark(
  client: StaffMatrixClient,
  handles: Map<string, ClientRowHandle>,
  t: (key: string) => string,
  locale: Locale
): string {
  const handle = handles.get(client.id);
  const mark = handle ? clientHandleText(handle, t, locale) : "";
  return mark === client.email ? "" : mark;
}

/** 🔴 Its own line, never appended to the address: that line ellipsizes. */
function ClientRecordMark(props: {
  client: StaffMatrixClient;
  handles: Map<string, ClientRowHandle>;
  t: (key: string) => string;
  locale: Locale;
}) {
  const mark = clientRecordMark(props.client, props.handles, props.t, props.locale);
  if (!mark) return null;
  return <span className="client-record-mark" data-client-handle>{mark}</span>;
}

/**
 * Who the client is waiting on right now. The server derives it from the
 * canonical step truths (shared/staff-lifecycle.ts) and the matrix only
 * CONSUMES it; the fallback covers cached payloads from before fixr4.
 */
export function matrixOwner(client: StaffMatrixClient): "partner" | "client" | "alex" {
  // The server derives the owner from the canonical truths; the list only
  // CONSUMES it. No raw-section fallback: a private derivation here is how
  // two surfaces start disagreeing again (fixr5, order 1).
  return client.owner ?? "client";
}

export function matrixSectionAge(client: StaffMatrixClient, locale: Locale, t: (key: string) => string): string {
  return t("staffConsole.phaseAge")
    .replace("{phase}", t(`partnerOS.phase.${client.currentPhase}`))
    .replace("{age}", translateCount(locale, "staffConsole.daysCount", client.phaseAgeDays));
}
export function Metric({ value, label, tone, active, onClick }: {
  value: number; label: string; tone?: "warn"; active?: boolean; onClick?: () => void;
}) {
  const empty = value === 0;
  // A zero-value filter cannot lead anywhere. Omitting it keeps the primary
  // row for actual operating signals instead of making empty cards dominant.
  if (empty) return null;
  const classes = [
    tone === "warn" && value > 0 ? "metric-warn" : "",
    active ? "metric-active" : "",
  ].filter(Boolean).join(" ");
  return (
    <article className={classes || undefined}>
      <button
        aria-pressed={active}
        className="metric-button"
        onClick={onClick}
        type="button"
      >
        <strong>{value}</strong><span>{label}</span>
      </button>
    </article>
  );
}
